import { useCallback, useEffect, useRef, useState } from "react";

/** useState that survives refreshes for this visit only (sessionStorage: erased when the tab closes; nothing goes to a server). */
export function useStored<T>(key: string, initial: T): [T, (v: T | ((p: T) => T)) => void, () => void] {
  const [value, setValue] = useState<T>(() => {
    try { const raw = sessionStorage.getItem(key); if (raw != null) return JSON.parse(raw) as T; } catch { /* private mode etc. */ }
    return initial;
  });
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return; }
    try { sessionStorage.setItem(key, JSON.stringify(value)); } catch { /* storage full or blocked */ }
  }, [key, value]);
  const reset = useCallback(() => { setValue(initial); try { sessionStorage.removeItem(key); } catch { /* ignore */ } }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  return [value, setValue, reset];
}

export interface Trip { name: string; start: string; days: string; travelers: string }
export const DEFAULT_TRIP: Trip = { name: "My Trip", start: "", days: "7", travelers: "1" };
/** One trip (name, departure, length, travellers) shared by all four travel tools. */
export const useTrip = () => useStored<Trip>("fdp-trip-v1", DEFAULT_TRIP);

/** "YYYY-MM-DD" → local Date at midnight (new Date("2026-10-30") would be UTC and shift a day in the Americas). */
export function parseLocalDate(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return d.getMonth() === Number(m[2]) - 1 ? d : null;
}
export function toIsoLocal(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
export function addDays(d: Date, n: number): Date { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n); }
/** Whole calendar days from a to b, ignoring time of day and DST. */
export function dayDiff(a: Date, b: Date): number {
  return Math.round((Date.UTC(b.getFullYear(), b.getMonth(), b.getDate()) - Date.UTC(a.getFullYear(), a.getMonth(), a.getDate())) / 86400000);
}
